import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'
import api from '@/lib/api'
import type { ActionItem } from '../types'

interface ActionItemsResponse {
  success: boolean
  items: ActionItem[]
}

interface GenerateActionItemsResponse {
  success: boolean
  created: number
  total: number
  items: ActionItem[]
}

export function useActionItems(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['action-items', sessionId],
    queryFn: async () => {
      if (!sessionId) throw new Error('Session ID is required')

      const response = await knowledgeApi.get<ActionItemsResponse>(
        `/knowledge/interactions/${sessionId}/action-items/`
      )

      return response.data
    },
    enabled: !!sessionId,
  })
}

export function useGenerateActionItems(sessionId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (!sessionId) throw new Error('Session ID is required')

      const response = await knowledgeApi.post<GenerateActionItemsResponse>(
        `/knowledge/interactions/${sessionId}/action-items/generate/`
      )

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-items', sessionId] })
    },
  })
}

export function useUpdateActionItemPriority() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      itemId,
      priority,
      sessionId: _sessionId,
    }: {
      itemId: number
      priority: string
      sessionId: string
    }) => {
      const response = await knowledgeApi.post(
        `/knowledge/interactions/action-items/${itemId}/priority/`,
        { priority }
      )

      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['action-items', variables.sessionId] })
    },
  })
}

export function useIgnoreActionItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ itemId, sessionId: _sessionId }: { itemId: number; sessionId: string }) => {
      const response = await knowledgeApi.post(
        `/knowledge/interactions/action-items/${itemId}/ignore/`
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['action-items', variables.sessionId] })
    },
  })
}

export function useAddToKanban() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ itemId, sessionId: _sessionId }: { itemId: number; sessionId: string }) => {
      const response = await api.post(
        `/actions/from-interaction/${itemId}/`
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['action-items', variables.sessionId] })
      queryClient.invalidateQueries({ queryKey: ['actions'] })
    },
  })
}
